import WebSocket from 'ws';
import type { LogFn } from '../../log.js';
import { sendWsMessage } from '../create-ws-bridge.js';
import { BRIDGE_APP_HEARTBEAT_INTERVAL_MS } from './constants.js';
import { buildBridgeHeartbeatMessage } from './build-heartbeat-message.js';
import { createHeartbeatSeqCursor } from './heartbeat-seq.js';
import { handlePendingHeartbeatMiss } from './handle-pending-heartbeat-miss.js';
import { createEveryNTicksRunner } from './on-every-n-ticks.js';
import { processHeartbeatAck } from './process-heartbeat-ack.js';
import { createRttSampleBuffer } from './rtt-samples.js';
import type { BridgeHeartbeatController, BridgeHeartbeatOnEveryNTicks } from './types.js';

export type { BridgeHeartbeatController } from './types.js';

/**
 * Sends compact `t:'h'` JSON heartbeats; closes the socket after
 * {@link BRIDGE_HEARTBEAT_MISSED_ACKS_BEFORE_RECONNECT} missed intervals without a qualifying ack.
 * Stale acks (`s` below the pending one) are ignored. A higher `s` clears the pending ping without
 * recording RTT (skipped pings are OK). RTT is averaged over the last {@link BRIDGE_HEARTBEAT_RTT_SAMPLE_MAX} samples.
 */
export function createBridgeHeartbeatController(params: {
  getWs: () => WebSocket | null;
  log: LogFn;
  /** Optional callback every N heartbeat ticks (see {@link BRIDGE_IDENTIFY_REFRESH_EVERY_TICKS}). */
  onEveryNTicks?: BridgeHeartbeatOnEveryNTicks;
}): BridgeHeartbeatController {
  const { getWs, log, onEveryNTicks } = params;
  let interval: ReturnType<typeof setInterval> | null = null;
  let awaitingSeq: number | null = null;
  let sentAtMs = 0;
  let missed = 0;
  const seqCursor = createHeartbeatSeqCursor();
  const rttSamples = createRttSampleBuffer();
  const everyNTicks = createEveryNTicksRunner(onEveryNTicks);

  function clearTimer(): void {
    if (interval != null) {
      clearInterval(interval);
      interval = null;
    }
  }

  function resetPendingState(): void {
    awaitingSeq = null;
    missed = 0;
    rttSamples.clear();
  }

  function tick(): void {
    const ws = getWs();
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const missResult = handlePendingHeartbeatMiss({
      awaitingSeq,
      missed,
      log,
      ws,
      onReconnect: () => {
        clearTimer();
        resetPendingState();
      },
    });
    missed = missResult.missed;
    if (missResult.stopTick) return;

    const seq = seqCursor.next();
    sendWsMessage(ws, buildBridgeHeartbeatMessage(seq, rttSamples.mean()));
    awaitingSeq = seq;
    sentAtMs = Date.now();
    everyNTicks.onTick();
  }

  return {
    start(): void {
      clearTimer();
      everyNTicks.reset();
      resetPendingState();
      seqCursor.reset();
      interval = setInterval(tick, BRIDGE_APP_HEARTBEAT_INTERVAL_MS);
    },
    stop(): void {
      clearTimer();
      resetPendingState();
    },
    onAck(seq: number): void {
      const result = processHeartbeatAck({
        seq,
        awaitingSeq,
        sentAtMs,
        recordRtt: rttSamples.push,
      });
      awaitingSeq = result.awaitingSeq;
      missed = result.missed;
    },
  };
}
